import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"
import {
  normalizeGoogleAdsLinkRows,
  normalizeGoogleAdsCostRows,
} from "../utils/googleAdsAudit"

const formatBoolean = (value) => {
  if (value === true) return "Yes"
  if (value === false) return "No"
  return "—"
}

const deriveLastUpdated = (rows) => {
  let latest = null
  for (const row of rows || []) {
    if (!row?.created_at) continue
    const parsed = Date.parse(row.created_at)
    if (Number.isNaN(parsed)) continue
    if (latest === null || parsed > latest) {
      latest = parsed
    }
  }
  return latest ? new Date(latest) : null
}

export default function GA4GoogleAdsAudit() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
    refresh: refreshProperty,
  } = useGa4Property(propertyId)
  const [rows, setRows] = useState([])
  const [costRows, setCostRows] = useState([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditError, setAuditError] = useState(null)
  const [optOut, setOptOut] = useState(false)
  const [optOutSaving, setOptOutSaving] = useState(false)
  const [optOutError, setOptOutError] = useState(null)

  useEffect(() => {
    setOptOut(Boolean(propInfo?.does_not_use_google_ads))
  }, [propInfo?.does_not_use_google_ads])

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setRows([])
      setCostRows([])
      setAuditError(propertyError || null)
      setAuditLoading(false)
      return
    }

    let cancelled = false

    const loadAudit = async () => {
      setAuditLoading(true)
      setAuditError(null)
      try {
        const [linksResult, costResult] = await Promise.all([
          supabase
            .from("ga4_google_ads_audit_results")
            .select(
              "link_resource_name, customer_id, creator_email, ads_personalization_enabled, created_at",
            )
            .eq("ga4_property_id", propInfo.id)
            .order("customer_id", { ascending: true }),
          supabase
            .from("ga4_google_ads_cost_audit_results")
            .select(
              "google_ads_customer_id, advertiser_ad_cost, currency_code, cost_period_start, cost_period_end, created_at",
            )
            .eq("ga4_property_id", propInfo.id)
            .order("google_ads_customer_id", { ascending: true }),
        ])

        const { data, error } = linksResult
        if (error) throw error
        const { data: costData, error: costError } = costResult
        if (costError) throw costError
        if (cancelled) return
        const normalizedRows = normalizeGoogleAdsLinkRows(data)
        setRows(normalizedRows)
        const normalizedCosts = normalizeGoogleAdsCostRows(costData)
        setCostRows(normalizedCosts)
      } catch (err) {
        if (cancelled) return
        console.error("[GA4] Failed to load Google Ads audit", err)
        setRows([])
        setCostRows([])
        setAuditError(err)
      } finally {
        if (!cancelled) setAuditLoading(false)
      }
    }

    loadAudit()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  const loading = propertyLoading || auditLoading

  const lastUpdated = useMemo(() => deriveLastUpdated(rows), [rows])
  const costLastUpdated = useMemo(() => deriveLastUpdated(costRows), [costRows])
  const consolidatedRows = useMemo(() => {
    if (!Array.isArray(rows) && !Array.isArray(costRows)) return []

    const joinKey = (value) => (value == null ? null : String(value))
    const linkMap = new Map()

    rows.forEach((row, index) => {
      const normalizedId = row?.normalized_customer_id ?? null
      const lookupKey = joinKey(normalizedId)
      if (lookupKey == null) return
      if (!linkMap.has(lookupKey)) linkMap.set(lookupKey, [])
      linkMap.get(lookupKey).push({ row, index })
    })

    const seenCostKeys = new Set()
    const result = []

    costRows.forEach((costRow, index) => {
      const normalizedId = costRow?.normalized_customer_id ?? null
      const key = joinKey(normalizedId)
      if (key != null) seenCostKeys.add(key)
      const linkEntry = key != null ? linkMap.get(key)?.[0] ?? null : null

      result.push({
        key: `cost-${key ?? "no-id"}-${index}`,
        customerId:
          linkEntry?.row?.customer_id ?? costRow?.google_ads_customer_id ?? null,
        creatorEmail: linkEntry?.row?.creator_email || null,
        adsPersonalization: linkEntry?.row?.ads_personalization_enabled,
        advertiserCost: costRow?.advertiser_ad_cost ?? null,
        currencyCode: costRow?.currency_code || null,
        periodStart: costRow?.cost_period_start || null,
        periodEnd: costRow?.cost_period_end || null,
        hasLinkedAccount: Boolean(linkEntry?.row),
      })
    })

    rows.forEach((linkRow, index) => {
      const normalizedId = linkRow?.normalized_customer_id ?? null
      const key = joinKey(normalizedId)
      if (key != null && seenCostKeys.has(key)) return
      result.push({
        key: `link-${key ?? "no-id"}-${index}`,
        customerId: linkRow?.customer_id || null,
        creatorEmail: linkRow?.creator_email || null,
        adsPersonalization: linkRow?.ads_personalization_enabled,
        advertiserCost: null,
        currencyCode: null,
        periodStart: null,
        periodEnd: null,
        hasLinkedAccount: true,
      })
    })

    return result.sort((a, b) => {
      const aId = a.customerId ? String(a.customerId) : ""
      const bId = b.customerId ? String(b.customerId) : ""
      return aId.localeCompare(bId, undefined, {
        numeric: true,
        sensitivity: "base",
      })
    })
  }, [rows, costRows])

  const flaggedCostRows = useMemo(
    () =>
      consolidatedRows.filter((row) => {
        const value =
          typeof row?.advertiserCost === "number"
            ? row.advertiserCost
            : null
        if (value == null || !Number.isFinite(value)) return false
        const hasLinked = Boolean(row?.hasLinkedAccount)
        const isPositiveLowSpend = value > 0 && value < 1
        if (hasLinked && isPositiveLowSpend) {
          return false
        }
        return value < 1
      }),
    [consolidatedRows],
  )
  const costPeriodRange = useMemo(() => {
    if (!Array.isArray(costRows) || costRows.length === 0) return null
    let start = null
    let end = null
    for (const row of costRows) {
      if (!start && typeof row?.cost_period_start === "string") {
        start = row.cost_period_start
      }
      if (!end && typeof row?.cost_period_end === "string") {
        end = row.cost_period_end
      }
      if (start && end) break
    }
    if (!start && !end) return null
    return { start: start || null, end: end || null }
  }, [costRows])

  const formatCurrency = (amount, currencyCode) => {
    if (typeof amount !== "number" || !Number.isFinite(amount)) return "—"
    const fallback = `${currencyCode || "USD"} ${amount.toFixed(2)}`
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode || "USD",
        maximumFractionDigits: 2,
      }).format(amount)
    } catch (err) {
      return fallback
    }
  }

  const formatPeriod = (start, end) => {
    if (start && end) return `${start} – ${end}`
    if (start) return start
    if (end) return end
    return "—"
  }

  const canEnableOptOut = useMemo(() => {
    if (!Array.isArray(costRows) || costRows.length === 0) return true
    return costRows.every((row) => {
      const value =
        typeof row?.advertiser_ad_cost === "number"
          ? row.advertiser_ad_cost
          : null
      if (value == null || !Number.isFinite(value)) return true
      return value < 1
    })
  }, [costRows])

  const handleOptOutToggle = async (event) => {
    if (!propInfo?.id) return
    const nextValue = event.target.checked
    if (nextValue && !canEnableOptOut) {
      return
    }
    setOptOutSaving(true)
    setOptOutError(null)
    try {
      const { error } = await supabase
        .from("ga4_properties")
        .update({ does_not_use_google_ads: nextValue })
        .eq("id", propInfo.id)
      if (error) throw error
      setOptOut(nextValue)
      await refreshProperty()
    } catch (err) {
      console.error("[GA4] Failed to update Google Ads opt-out flag", err)
      setOptOutError(
        err?.message || "Failed to update Google Ads preference.",
      )
    } finally {
      setOptOutSaving(false)
    }
  }

  return (
    <div className="page-container">
      <h2>Google Ads Audit</h2>
      <button
        type="button"
        onClick={() =>
          navigate(`/ga4-property?id=${encodeURIComponent(propertyId || "")}`)
        }
      >
        Back
      </button>
      {loading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : auditError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load Google Ads audit data.
        </p>
      ) : (
        <>
          {propInfo ? (
            <div style={{ marginTop: "1rem" }}>
              <h3 style={{ margin: 0 }}>
                {propInfo.property_name}
                <span
                  style={{
                    fontWeight: "normal",
                    fontSize: "0.9em",
                    marginLeft: "0.5rem",
                  }}
                >
                  ({propInfo.property_id})
                </span>
              </h3>
            </div>
          ) : (
            <p style={{ marginTop: "1rem" }}>Property not found.</p>
          )}

          <div
            style={{
              marginTop: "1rem",
              display: "flex",
              flexWrap: "wrap",
              gap: "1.5rem",
              alignItems: "center",
            }}
          >
            <div>
              <strong>Linked accounts:</strong>{" "}
              {rows.length ? rows.length : "—"}
            </div>
            <div>
              <strong>Links updated:</strong>{" "}
              {lastUpdated
                ? lastUpdated.toLocaleString()
                : rows.length
                ? "Unknown"
                : "—"}
            </div>
            <div>
              <strong>Cost rows:</strong>{" "}
              {costRows.length ? costRows.length : "—"}
            </div>
            <div>
              <strong>Cost updated:</strong>{" "}
              {costLastUpdated
                ? costLastUpdated.toLocaleString()
                : costRows.length
                ? "Unknown"
                : "—"}
            </div>
            <div>
              <strong>Flagged CIDs:</strong>{" "}
              {optOut ? "Ignored" : flaggedCostRows.length}
            </div>
          </div>

          {costPeriodRange && (
            <div style={{ marginTop: "0.5rem" }}>
              <strong>Cost period:</strong>{" "}
              {formatPeriod(costPeriodRange.start, costPeriodRange.end)}
            </div>
          )}

          <div
            style={{
              marginTop: "1rem",
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              alignItems: "center",
            }}
          >
            <label
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                fontWeight: 500,
                cursor: optOutSaving ? "progress" : "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={optOut}
                onChange={handleOptOutToggle}
                disabled={
                  optOutSaving ||
                  !propInfo?.id ||
                  (!optOut && !canEnableOptOut)
                }
              />
              Does Not Use Google Ads
            </label>
            {optOutSaving && (
              <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>
                Saving preference…
              </span>
            )}
          </div>

          {optOutError && (
            <p style={{ marginTop: "0.5rem", color: "#b91c1c" }}>{optOutError}</p>
          )}
          {!optOut && !canEnableOptOut && (
            <p style={{ marginTop: "0.5rem", color: "#6b7280" }}>
              Spend of $1 or more was detected. This property cannot be marked as not
              using Google Ads.
            </p>
          )}
          {optOut && (
            <p style={{ marginTop: "0.5rem", color: "#1f2937" }}>
              Google Ads cost issues are disabled for this property.
            </p>
          )}

          {consolidatedRows.length ? (
            <div style={{ marginTop: "1.25rem" }}>
              <table className="ui-table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  tableLayout: "auto",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "0.5rem",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      Linked Customer ID
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "0.5rem",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      Creator Email
                    </th>
                    <th
                      style={{
                        textAlign: "center",
                        padding: "0.5rem",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      Ads Personalization
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "0.5rem",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      Advertiser Cost
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "0.5rem",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      Period
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "0.5rem",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {consolidatedRows.map((row) => {
                    const value =
                      typeof row.advertiserCost === "number"
                        ? row.advertiserCost
                        : null
                    const hasSpendValue = value != null && Number.isFinite(value)
                    const hasLinkedAccount = Boolean(row.hasLinkedAccount)
                    const isPositiveLowSpend =
                      hasSpendValue && value > 0 && value < 1
                    const isIssue =
                      hasSpendValue &&
                      value < 1 &&
                      !(hasLinkedAccount && isPositiveLowSpend)
                    const statusLabel = hasSpendValue
                      ? optOut
                        ? "Ignored (Does Not Use Google Ads)"
                        : isIssue
                        ? "Issue: Spend < $1"
                        : "OK"
                      : "No spend data"
                    const rowStyle =
                      !optOut && isIssue
                        ? { backgroundColor: "rgba(248, 113, 113, 0.18)" }
                        : {}
                    const statusStyle = hasSpendValue
                      ? optOut
                        ? { color: "#4b5563" }
                        : isIssue
                        ? { color: "#b91c1c", fontWeight: 600 }
                        : { color: "#047857", fontWeight: 600 }
                      : { color: "#4b5563" }

                    return (
                      <tr key={row.key} style={rowStyle}>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid #e2e8f0",
                            wordBreak: "break-word",
                          }}
                        >
                          {row.customerId || "—"}
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid #e2e8f0",
                            wordBreak: "break-word",
                          }}
                        >
                          {row.creatorEmail || "—"}
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid #e2e8f0",
                            textAlign: "center",
                          }}
                        >
                          {formatBoolean(row.adsPersonalization)}
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid #e2e8f0",
                            textAlign: "right",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatCurrency(value, row.currencyCode)}
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid #e2e8f0",
                            wordBreak: "break-word",
                          }}
                        >
                          {formatPeriod(row.periodStart, row.periodEnd)}
                        </td>
                        <td
                          style={{
                            padding: "0.5rem",
                            borderBottom: "1px solid #e2e8f0",
                            ...statusStyle,
                          }}
                        >
                          {statusLabel}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ marginTop: "1.25rem" }}>
              No Google Ads links or cost data were found for this property.
            </p>
          )}
        </>
      )}
    </div>
  )
}
